import { useCallback, useEffect, useState } from 'react';

/**
 * Confirmation intégrée à l'application, en remplacement de `window.confirm`.
 *
 * Le dialogue natif peut être **supprimé par le navigateur** — Chrome le fait dès
 * qu'on coche « Empêcher cette page de créer des boîtes de dialogue », et dans
 * certaines fenêtres d'application. Il renvoie alors `false` sans rien afficher :
 * le code sortait en silence, le bouton paraissait mort et aucune requête ne
 * partait. C'est ce qui a bloqué la validation de la commande S00189 le
 * 2026-10-14 — aucune trace côté serveur, puisque le clic n'arrivait jamais.
 *
 * Ici l'affichage est du DOM ordinaire : rien ne peut le désactiver.
 */

type Demande = { message: string; resolve: (ok: boolean) => void };

let ouvrir: ((d: Demande) => void) | null = null;

/** À utiliser partout à la place de `confirm()` : `if (!(await confirmer(msg))) return;` */
export function confirmer(message: string): Promise<boolean> {
  if (!ouvrir) {
    // L'hôte n'est pas monté (ne devrait pas arriver) : on ne bloque pas l'action
    // en silence, on retombe sur le dialogue natif.
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => ouvrir!({ message, resolve }));
}

/** Monté une seule fois, au-dessus des routes. */
export default function ConfirmHost() {
  const [d, setD] = useState<Demande | null>(null);

  useEffect(() => {
    ouvrir = setD;
    return () => { ouvrir = null; };
  }, []);

  const repondre = useCallback((ok: boolean) => {
    setD((cur) => { cur?.resolve(ok); return null; });
  }, []);

  useEffect(() => {
    if (!d) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') repondre(false);
      if (e.key === 'Enter') repondre(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [d, repondre]);

  if (!d) return null;
  return (
    <div className="confirm-backdrop" onClick={() => repondre(false)}>
      <div className="confirm-card" role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <p className="confirm-message">{d.message}</p>
        <div className="confirm-actions">
          <button className="devis-refresh" onClick={() => repondre(false)}>Annuler</button>
          <button className="devis-action" autoFocus onClick={() => repondre(true)}>Confirmer</button>
        </div>
      </div>
    </div>
  );
}
